/**
 * # LoadingSpinner
 *
 * A component that displays a loading spinner with optional text.
 *
 * ## Props
 *
 * - `assistiveText?: string` (optional): The text to be displayed for screen readers.
 * - `className?: string` (optional): Additional CSS class names for the component.
 * - `size?: 'lg' | 2 | 3 | 4 | 5` (optional): The size of the spinner. Can be one of 'lg', 2, 3, 4, or 5.
 * - `icon?: IconProp` (optional): FontAwesome icon name to render. Defaults to `'spinner'`.
 *   When `'spinner'`, uses the `pulse` animation; any other icon uses the smooth `spin`.
 * - `iconStyle?: IconStyle` (optional): FontAwesome style (`'light' | 'regular' | 'solid'`).
 *   Forwarded to the underlying `Icon` atom — make sure the chosen `icon` is registered in
 *   the matching style in `helpers/FontAwesome.js`.
 *
 * ## Usage
 *
 * ```tsx
 * import LoadingSpinner from './LoadingSpinner.react';
 *
 * const ExampleComponent = () => {
 *   return (
 *     <div>
 *       <LoadingSpinner assistiveText="Loading..." size="lg" />
 *       <LoadingSpinner assistiveText="Please wait..." size={3} />
 *       <LoadingSpinner assistiveText="Loading..." icon="loader" iconStyle="light" size={3} />
 *     </div>
 *   );
 * };
 * ```
 */

import React from 'react';
import classnames from 'classnames';

import type {IconProp, IconStyle} from '../../atoms/Icon/Icon.react';
import Icon from '../../atoms/Icon/Icon.react';

interface Props extends PropsWithClassNameOptional {
  assistiveText?: string;
  bottomMarginLevel?: number;
  size?: 'lg' | 2 | 3 | 4 | 5;
  icon?: IconProp;
  iconStyle?: IconStyle;
}

const LoadingSpinner = ({
  assistiveText = 'Content is loading...',
  bottomMarginLevel = 2,
  className,
  size,
  icon = 'spinner',
  iconStyle,
  ...rest
}: Props) => {
  const usePulse = icon === 'spinner';
  return (
    <div className={classnames(className, 'flex justify-center', `mb-x${bottomMarginLevel}`)}>
      <Icon
        className={classnames('fa-fw', {
          'fa-lg': size === 'lg',
          'fa-2x': size === 2,
          'fa-3x': size === 3,
          'fa-4x': size === 4,
          'fa-5x': size === 5
        })}
        icon={icon}
        iconStyle={iconStyle}
        pulse={usePulse}
        spin={!usePulse}
        {...rest}
      />
      <label className='sr-only'>
        <progress />
        {assistiveText}
      </label>
    </div>
  );
};

LoadingSpinner.displayName = 'LoadingSpinner';

export default LoadingSpinner;
